<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue';
import { RouterLink } from 'vue-router';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';
import { useToast } from "primevue/usetoast";
import { useCommentsStore } from '@/stores/comment.js';

const props = defineProps({
  target: { type: String, required: true },           // 'customer' | 'work' | 'facility' | 'contract'
  entityId: { type: [String, Number], required: true },
  currentUserName: { type: String, default: 'Вы' },
});

const toast = useToast();
const commentsStore = useCommentsStore();

const commentText = ref('');
const error = ref('');

const thread = computed(() => commentsStore.thread(props.target, props.entityId));
const loading = computed(() => thread.value.loading);
const hasMore = computed(() => commentsStore.hasMore(props.target, props.entityId));

// Локальное состояние разворачивания — Set id-шников (не мутируем стор)
const expandedIds = ref(new Set());
const isExpanded = (id) => expandedIds.value.has(id);
const toggleExpanded = (id) => {
  if (expandedIds.value.has(id)) expandedIds.value.delete(id);
  else expandedIds.value.add(id);
};

async function initialLoad() {
  try {
    expandedIds.value.clear();
    await commentsStore.loadComments({ target: props.target, id: props.entityId, page: 1 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить комментарии', life: 3000 });
  }
}

watch(() => [props.target, props.entityId], () => initialLoad(), { immediate: true });
onMounted(initialLoad);

// Контейнер со скроллом и бесконечная подгрузка
const scrollContainer = ref(null);
const isFetchingMore = ref(false);

async function tryLoadMore() {
  if (isFetchingMore.value || loading.value || !hasMore.value) return;
  isFetchingMore.value = true;
  try {
    await commentsStore.loadMore({ target: props.target, id: props.entityId });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось подгрузить комментарии', life: 3000 });
  } finally {
    isFetchingMore.value = false;
  }
}

function onScroll() {
  const el = scrollContainer.value;
  if (!el) return;
  // Порог «почти у дна»
  const threshold = 80;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  if (nearBottom) {
    tryLoadMore();
  }
}

onMounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', onScroll, { passive: true });
  }
});
onBeforeUnmount(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', onScroll);
  }
});

function getShortText(text, expanded) {
  const t = String(text || '');
  if (expanded) return t;
  const head = t.split('\n').slice(0, 2).join('\n');
  return head.length > 90 ? head.substring(0, 90) + '...' : head;
}

async function onSubmit(e) {
  e.preventDefault();
  error.value = '';
  const body = String(commentText.value || '').trim();
  if (!body) {
    error.value = 'Введите текст комментария';
    return;
  }
  try {
    const created = await commentsStore.addComment({ target: props.target, id: props.entityId, body });
    if (created?.id != null) expandedIds.value.add(created.id);
    commentText.value = '';
    // При добавлении — автоматически прокручивать не будем, чтобы не сбивать пользователя
    toast.add({ severity: 'success', summary: 'Комментарий добавлен', life: 2000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось добавить комментарий', life: 3500 });
  }
}

async function removeComment(id) {
  try {
    await commentsStore.deleteComment({ target: props.target, id: props.entityId, commentId: id });
    expandedIds.value.delete(id);
    toast.add({ severity: 'success', summary: 'Комментарий удалён', life: 2000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить комментарий', life: 3500 });
  }
}
</script>

<template>
  <Card>
    <template #content>
      <form @submit="onSubmit" class="flex flex-col gap-4 pb-4 flex-shrink-0">
        <div class="flex flex-col gap-1">
          <Textarea
              v-model="commentText"
              rows="3"
              autoResize
              class="w-full"
              placeholder="Введите комментарий"
              style="resize: none"
          />
          <Message v-if="error" severity="error" size="small" variant="simple">{{ error }}</Message>
        </div>
        <Button type="submit" severity="info" variant="outlined" rounded label="Добавить" />
      </form>

      <!-- Собственный контейнер со скроллом -->
      <div ref="scrollContainer" class="flex-1 min-h-0 overflow-auto">
        <div v-for="c in thread.results" :key="c.id" class="mb-2 relative px-1">
          <div class="flex justify-between items-center">
            <div class="flex flex-col">
              <RouterLink
                  v-if="c.author_id"
                  :to="`/employees/${c.author_id}`"
                  class="font-semibold"
              >
                {{ c.author || 'Пользователь' }}
              </RouterLink>
              <span v-else class="font-semibold">{{ c.author || 'Пользователь' }}</span>              <span class="text-gray-400 text-xs">{{ new Date(c.created_at).toLocaleString('ru-RU') }}</span>
            </div>
            <Button
                icon="pi pi-times"
                severity="warn"
                variant="text"
                size="small"
                rounded
                @click="removeComment(c.id)"
            />
          </div>
          <div class="mt-2 whitespace-pre-line text-sm min-h-[2.5em]">
            {{ getShortText(c.body, isExpanded(c.id)) }}
          </div>
          <div v-if="(c.body || '').length > 90" class="text-center">
            <Button
                :icon="'pi pi-chevron-' + (isExpanded(c.id) ? 'up' : 'down')"
                severity="secondary"
                variant="text"
                size="small"
                @click="toggleExpanded(c.id)"
                aria-label="развернуть/свернуть комментарий"
            />
          </div>
        </div>

        <!-- Индикатор/хвост -->
        <div class="py-2 text-center text-sm text-gray-500">
          <span v-if="loading">Загрузка...</span>
          <span v-else-if="!hasMore && (thread.results?.length || 0) > 0">Это все комментарии</span>
          <span v-else-if="(thread.results?.length || 0) === 0">Комментариев пока нет</span>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped>
:deep(.p-card) {
  display: flex;
  flex-direction: column;
}
:deep(.p-card-body),
:deep(.p-card-content) {
  display: flex;
  flex-direction: column;
  /* на мобильных не форсируем высоту */
}


@media (min-width: 1024px) {
  :deep(.p-card) { height: 100%; }
  :deep(.p-card-body),
  :deep(.p-card-content) {
    flex: 1;
    min-height: 0;
  }
}

</style>